import { readFile } from "@ramonak/react-excel";
import React, { useState } from "react";
import { Button, Progress, UncontrolledTooltip } from "reactstrap";
import { api } from "services/api";
import ProcessBox from "views/pages/components/custom/loader/processingLoader";
import ImportSummary from "views/pages/components/custom/loader/importSummary";
import { useImportJobPolling } from "hooks/useImportJobPolling";
import feedback from "../../../../services/feedback";

import "./styles/style-duelist-upload-card.css";

// issue #31: mensagem de duplicado no mesmo formato que o componente ja usava,
// so que agora a origem do dado e o payload de erro do job (ou o corpo do 409).
const buildDuplicatedOrderMessage = (so, item) =>
    `Upload Error. Was found duplicated order: ${so}${item}`;

// issue #31: a barra de status dizia "Upload error." em todos os caminhos de
// falha, e o motivo real so aparecia num alert que some ao ser fechado.
// Quando a importacao falhou em producao nao foi possivel saber a causa pela
// tela. Estas mensagens deixam o motivo visivel na propria barra.
const MAX_STATUS_MESSAGE = 90;

const summarizeError = (rawMessage, fallback) => {
    if (!rawMessage) return fallback;
    // error_message do job pode vir com traceback inteiro: fica so a 1a linha util
    const firstLine = String(rawMessage).split("\n").find((line) => line.trim());
    if (!firstLine) return fallback;
    const clean = firstLine.trim();
    return clean.length > MAX_STATUS_MESSAGE ? `${clean.slice(0, MAX_STATUS_MESSAGE)}...` : clean;
};

export const UploadCard = () => {

    const [ fileData, setFileData ] = useState(null);
    const [ excelData, setExcelData ] = useState(null);
    const [ sheetName, setSheetName ] = useState(null);
    const [ importState, setImportState ] = useState(false);
    const [ loader, setLoader ] = useState({
        status: false,
        message: "",
        // issue #31: fase e statusDoJob alimentam o trilho de etapas do ProcessBox,
        // para a tela distinguir "enviando" de "na fila" de "importando".
        fase: null,
        statusDoJob: null,
        progresso: null,
    });
    const [ uploadTime, setUploadTime ] = useState({
        time: 0,
        message: null,
        color: "default"
    });

    // issue #31: guarda o desfecho da importacao para o painel de resumo, que
    // substitui o "Upload success."/"Upload error." soltos na barra.
    const [ resultado, setResultado ] = useState(null);

    const { startPolling, stopPolling } = useImportJobPolling();

    const handlerInputFile = async (e) => {
        // Sem isso o poll do upload anterior continua vivo e sobrescreve a tela
        // depois que o usuario ja trocou de arquivo (issue #31).
        stopPolling();
        setResultado(null);
        setLoader({status: true, message: "Getting sheet names..."});
        setUploadTime({ time: 0, message: null, color: "default" });
        setSheetName("");
        setImportState(false);
        if(e.target.files.length > 0){
            const file = e.target.files[0];
            file.format = file.name.split('.');
            setFileData(file);

            try {
                const data = await readFile(file);
                setExcelData({
                    sheets: data.SheetNames
                });
                setLoader({status: false, message: ""});
            } catch (error) {
                console.error(error);
                setLoader({status: false, message: ""});
            }
        }
    }

    const handlerInputRadio = (e) => {
        if(e.target.checked){
            setSheetName(e.target.value);
        }
    }

    const showUploadResult = (message, color) => {
        setUploadTime({ time: 100, message, color });
        setLoader({ status: false, message: "", fase: null, statusDoJob: null, progresso: null });
    }

    const handleJobSuccess = (job) => {
        showUploadResult("Upload success.", "success");
        let resumo = null;
        try { resumo = job.result_summary ? JSON.parse(job.result_summary) : null; } catch (e) { resumo = null; }
        setResultado({ tipo: "sucesso", resumo, arquivo: fileData ? fileData.name : null });
    }

    const handleJobFailed = (job) => {
        const payload = job.error_payload && JSON.parse(job.error_payload);
        if (payload && payload.status_error === "logistic_map_IntegrityError") {
            showUploadResult(`Duplicated order: ${payload.so}${payload.item}`, "danger");
            return setResultado({ tipo: "erro", mensagem: buildDuplicatedOrderMessage(payload.so, payload.item), arquivo: fileData ? fileData.name : null });
        }
        showUploadResult(summarizeError(job.error_message, "Import failed."), "danger");
        setResultado({ tipo: "erro", mensagem: job.error_message, arquivo: fileData ? fileData.name : null });
    }

    const handleJobTimeout = () => {
        showUploadResult("Timed out waiting for the import job.", "danger");
        setResultado({ tipo: "erro", mensagem: "The import job took too long to finish.", arquivo: fileData ? fileData.name : null });
    }

    const handlePollError = (error) => {
        // Falha ao CONSULTAR o status: o job pode ate ter terminado bem no servidor.
        showUploadResult("Lost connection while checking the import status.", "danger");
        console.error(error);
        setResultado({ tipo: "erro", mensagem: "Conexao perdida ao acompanhar a importacao. O processamento pode ter continuado no servidor.", arquivo: fileData ? fileData.name : null });
    }

    // pending/processing: mantem o ProcessBox visivel enquanto o worker trabalha.
    const handleJobTick = (job) => {
        const aviso = job.status === "processing"
            ? "Gravando os dados no sistema."
            : "A importacao entrou na fila e comeca em instantes.";
        setLoader({ status: true, message: aviso, fase: null, statusDoJob: job.status, progresso: null });
    }

    const handleUploadProgress = (event) => {
        const progress = Math.round((event.loaded * 100) / event.total);
        if (progress === 100) {
            // Backend terminou de receber o arquivo e vai enfileirar o job. Aqui o
            // ProcessBox substitui o card inteiro, entao mexer em uploadTime seria
            // estado morto: quem da o feedback deste instante e o loader.
            return setLoader({ status: true, message: "Arquivo recebido, criando a importacao.", fase: "fila", statusDoJob: "pending", progresso: 100 });
        }
        setLoader({ status: true, message: "Nao feche esta aba enquanto o arquivo sobe.", fase: "envio", statusDoJob: null, progresso: progress });
        return setUploadTime((prev) => ({ ...prev, time: progress }));
    }

    const describeEnqueueFailure = (request) => {
        // Sem corpo: rede fora, timeout de gateway ou servidor caiu.
        if (!request.response) return "Could not reach the server.";
        const response = request.response.data;
        if (!response) return `Server error (HTTP ${request.response.status}).`;
        if (Object.keys(response).indexOf("error") > -1) {
            return summarizeError(response.error, "Upload error.");
        }
        return `Upload failed (HTTP ${request.response.status}).`;
    }

    // Duplicidade de SO nao aparece aqui: o enqueue so valida e grava o job, e a
    // importacao (onde o IntegrityError acontece) roda depois no worker, entao
    // esse caso chega pelo polling em handleJobFailed (issue #31).
    const handleEnqueueError = (request) => {
        console.error(request);
        const detalhe = request.response && request.response.data;
        showUploadResult(describeEnqueueFailure(request), "danger");
        feedback.notify({
            type: "danger",
            message: detalhe && detalhe.error
                ? detalhe.error
                : "Undefined error... Please, contact the support!"
        });
    }

    // issue #31: o POST deixou de gravar os dados (levava ~20min e estourava o
    // timeout do gunicorn). Agora ele so enfileira o job e devolve o job_id; o
    // trabalho pesado e acompanhado por polling em GET import-jobs/<id>/.
    const sendExcelFile = () => {
        if(!fileData) return feedback.notify({ type: "warning", message: "Please, select a excel file to upload." });
        else if(!sheetName) return feedback.notify({ type: "warning", message: "Please, select a sheet to read." });

        // Um segundo envio nao pode conviver com o poll do primeiro: se o enqueue
        // falhar, o job antigo terminaria sobrescrevendo o erro na tela (issue #31).
        stopPolling();
        setResultado(null);
        setUploadTime({ time: 0, message: null, color: "default" });

        const kind = importState ? "logisticMap" : "zzorder";
        api.post(`/upload/${kind}/enqueue/${sheetName}`, fileData, {
            onUploadProgress: handleUploadProgress
        })
        .then((request) => startPolling(request.data.job_id, {
            onSuccess: handleJobSuccess,
            onFailed: handleJobFailed,
            onTimeout: handleJobTimeout,
            onPollError: handlePollError,
            onTick: handleJobTick
        }))
        .catch(handleEnqueueError);
    }

    const handlerImportCheck = (e) => {
        setImportState(e.target.checked);
    }

    return (
        <>
            {resultado ? (
                <ImportSummary
                    tipo={resultado.tipo}
                    arquivo={resultado.arquivo}
                    resumo={resultado.resumo}
                    mensagem={resultado.mensagem}
                    aoFechar={() => setResultado(null)}
                />
            ) : loader.status ? (
                <ProcessBox
                    message={loader.message}
                    fase={loader.fase}
                    statusDoJob={loader.statusDoJob}
                    progresso={loader.progresso}
                    arquivo={fileData ? fileData.name : null}
                />
            ) : (
                <div className="duelist-upload-container-card">
                    <div className="duelist-upload-card">
                        <label htmlFor="file-seletor" className="duelist-upload-card-label">
                            Click to Upload
                            <div className="ni ni-send duelist-upload-card-icon"/>
                        </label>
                        <div className="duelist-upload-card-title">
                            File Name:
                        </div>
                        <div className="duelist-upload-card-value" id="file-target">
                            {fileData?(
                                <>
                                    {fileData.format[0]}&nbsp;
                                </>
                            ):"No excel selected."}
                        </div>
                        <UncontrolledTooltip
                            delay={0}
                            placement="right"
                            target="file-target"
                        >
                            {fileData?fileData.format[0]:"No excel selected."}
                        </UncontrolledTooltip>
                        <div className="duelist-upload-card-title">
                            Sheets:
                        </div>
                        <div className="duelist-upload-card-container-sheets">
                            <div className="duelist-upload-card-group-sheets">
                                {excelData?excelData.sheets.map((sheet, index) => (
                                    <div key={`sheet-name-${index}`} className="duelist-upload-card-sheet-item">
                                        <input type="radio" onChange={handlerInputRadio} id={`sheet-item-${index}`} value={sheet} className="duelist-upload-card-sheet-radio"/>
                                        <label htmlFor={`sheet-item-${index}`} className="duelist-upload-card-sheet-label">
                                            {sheet}
                                        </label>                 
                                    </div>
                                )):null}
                            </div>
                        </div>
                        <div className="duelist-upload-card-container-progress">
                            <div className="progress-info">
                                <div className="progress-percentage">
                                    <span>
                                        {uploadTime.message?uploadTime.message:uploadTime.time}
                                    </span>
                                </div>
                            </div>
                            <Progress max="100" value={uploadTime.time} color={uploadTime.color}/>
                            <div className="duelist-upload-card-check-file" style={{
                                height: [sheetName?"auto":"0px"],
                            }}>
                                <label htmlFor="switch-buttom-file">
                                    ZZORDER
                                </label>
                                <label className="switch">
                                    <input type="checkbox" onChange={handlerImportCheck} id="switch-buttom-file"/>
                                    <span className="slider round"/>
                                </label>
                                <label htmlFor="switch-buttom-file">
                                    DUE LIST
                                </label>
                            </div>
                        </div>
                        <input type="file" id="file-seletor" onChange={handlerInputFile} className="duelist-upload-card-input-file" accept=".xlsx, .XLSX, .xls, .XLS"/>
                    </div>
                    <Button color="primary" type="button" onClick={sendExcelFile}>
                        Send excel file
                    </Button>
                </div>
            )}
        </>
    )
}